import Skeleton from "@components/skeleton/Skeleton";
import { composeRefHandlers, doesElementContainsElement, isDefined } from "@utils/general";
import { KeyboardShortcut } from "@utils/keyboardShortcutsManager/KeyboardShorcutsManager.utils";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { EditableTextSizes, Status } from "../../../enums";
import { KeyName } from "../../../keyName";
import { IAuditTrailData } from "../../../model/Model";
import TestIds from "../../../testIds";
import KeyboardShortcutsManager from "../../../utils/keyboardShortcutsManager/KeyboardShortcutsManager";
import { RequiredMark } from "../field/Label.styles";
import { WithErrorAndTooltipProps } from "../formError/WithErrorAndTooltip";
import { IInputOnBlurEvent, IInputOnChangeEvent, ISharedInputProps } from "../input";
import { InputStatusStraight } from "../input/Input.styles";
import TextArea from "../textArea";
import { ReadOnlyText } from "../utils";
import {
    EditableTextConfirmButtons,
    ReadLabel,
    ReadLabelText,
    StyledEditableText,
    TextAreaWrapper,
    Wrapper
} from "./EditableText.styles";

export interface IEditableTextChangeEvent {
    value: string;
}

export interface IEditableTextProps extends ISharedInputProps, WithErrorAndTooltipProps {
    /** Value of the text area*/
    value: string;
    placeholder: string;
    isResponsive?: boolean;
    isInEdit?: boolean;
    isDisabled?: boolean;
    isMultiLine?: boolean;
    isConfirmable?: boolean;
    isSkeleton?: boolean;
    showRequiredMark?: boolean;
    maxRows?: number;
    style?: React.CSSProperties;
    onConfirm?: () => void;
    onCancel?: () => void;
    onChange?: (args: IEditableTextChangeEvent) => void;
    onBlur?: (args: IInputOnBlurEvent) => void;
    onIsEditChanged?: (isInEdit: boolean) => void;
    auditTrailData?: IAuditTrailData;
    passRef?: React.Ref<HTMLTextAreaElement | HTMLDivElement>;
    outerRef?: React.RefObject<HTMLDivElement>;
}

interface IPropsSkeleton extends Partial<IEditableTextProps> {
    isSkeleton: true;
}

type IProps = (IEditableTextProps | IPropsSkeleton) & WithTranslation & {
    className?: string;
};


interface IEditableTextState {
    isInEdit: boolean;
}

const MAX_ROWS_SMALL = 12;
const MAX_ROWS_MEDIUM = 16;
const MAX_ROWS_LARGE = 20;

class EditableText extends React.Component<IProps, IEditableTextState> {
    textAreaRef = React.createRef<HTMLTextAreaElement>();
    buttonsWrapper = React.createRef<HTMLDivElement>();
    _wasChangedSinceBlur = false;

    state: IEditableTextState = {
        isInEdit: false
    };

    componentDidUpdate(prevProps: IProps) {
        if (this.props.value !== prevProps.value) {
            this._wasChangedSinceBlur = true;
        }
    }

    get width() {
        return this.props.width || EditableTextSizes.M;
    }

    get maxRowCount() {
        if (this.props.maxRows) {
            return this.props.maxRows;
        }

        if (!this.props.isMultiLine) {
            return 1;
        }

        const width = this.width;

        if (width === EditableTextSizes.S) {
            return MAX_ROWS_SMALL;
        }

        if (width === EditableTextSizes.M) {
            return MAX_ROWS_MEDIUM;
        }

        if (width === EditableTextSizes.L) {
            return MAX_ROWS_LARGE;
        }

        return MAX_ROWS_SMALL;
    }

    get isInEdit() {
        // with error prop it's always rendered in edit mode as we are not able to display the error otherwise
        return !!this.props.error || this.props.isInEdit || this.state.isInEdit;
    }

    async autoConfirmOnBlur(e: React.FocusEvent): Promise<void> {
        const target = e.relatedTarget as HTMLDivElement;
        const contains = doesElementContainsElement(this.textAreaRef.current, target) || doesElementContainsElement(this.buttonsWrapper.current, target);

        if (!contains) {
            await this.handleConfirm();
        }
    }

    switchToNonEditableState(): Promise<void> {
        // await so that we can wait for the state to be updated in the caller functions
        return new Promise((resolve) => {
            if (this.state.isInEdit) {
                this.setState({
                    isInEdit: false
                }, () => {
                    this.props.onIsEditChanged?.(false);
                    resolve(null);
                });
            } else {
                resolve(null);
            }
        });
    }

    handleChange = (e: IInputOnChangeEvent): void => {
        this.props.onChange({
            value: e.value as string
        });
    };

    handleBlur = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
        // ?. can make null -> undefined and mess up comparison
        const trimmedValue = isDefined(this.props.value) ? this.props.value?.trim() : this.props.value;
        if (this.props.value !== trimmedValue) {
            // todo: general solution to all inputs and textareas?
            this.props.onChange({
                value: trimmedValue
            });
        }
        if (!this.props.isConfirmable) {
            await this.switchToNonEditableState();

            this.props.onBlur?.({
                origEvent: e as unknown as React.FocusEvent<HTMLInputElement>,
                wasChanged: this._wasChangedSinceBlur
            });
        } else {
            await this.autoConfirmOnBlur(e);
        }

        this._wasChangedSinceBlur = false;
    };

    handleButtonsBlur = (e: React.FocusEvent) => {
        if (this.props.isConfirmable) {
            this.autoConfirmOnBlur(e);
        }
    };

    handleClick = () => {
        if (!this.state.isInEdit) {
            this.setState({
                isInEdit: true
            }, () => {
                this.props.onIsEditChanged?.(true);
                this.textAreaRef.current.focus();
            });
        }
    };

    handleConfirm = async () => {
        this.props.onConfirm?.();
        await this.switchToNonEditableState();
    };

    handleCancel = async () => {
        this.props.onCancel?.();
        await this.switchToNonEditableState();
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (true) {
            case event.key === KeyName.Enter:
                if (event.target === event.currentTarget) {
                    this.handleClick();
                    event.preventDefault();
                }
                break;
            case event.key === KeyName.Escape:
                this.handleCancel();
                event.preventDefault();
                break;
            case KeyboardShortcutsManager.isEventShortcut(event, KeyboardShortcut.ALT_S):
                if (this.props.isConfirmable) {
                    this.handleConfirm();
                }
                break;
        }
    };

    renderEditableText() {
        const value = this.props.value === null ? "" : this.props.value;
        const width = !this.isInEdit && !value ? "fit-content" : this.props.width;
        return (
            <StyledEditableText
                ref={this.props.outerRef}
                className={this.props.className}
                _isResponsive={this.props.isResponsive}
                _width={width}
                tabIndex={this.props.isDisabled || this.props.auditTrailData ? null : 0}
                auditTrailType={this.props.auditTrailData?.type}
                onKeyDown={this.handleKeyDown}
                isInEdit={this.isInEdit}
                isDisabled={this.props.isDisabled}
                data-testid={TestIds.EditableText}>
                <Wrapper
                    _hoverable
                    _width={this.width}
                    _isReponsive={this.props.isResponsive}
                    onClick={this.handleClick}
                    isInEdit={this.isInEdit}
                    style={this.props.style}>
                    {this.props.showRequiredMark && !this.isInEdit &&
                        <RequiredMark style={{ top: "5px", left: "10px" }}/>
                    }
                    {!this.isInEdit &&
                        <ReadLabel
                            ref={this.props.passRef as React.Ref<HTMLDivElement>}
                            _hoverable
                            title={value}>
                            {/*TODO maybe use <Text> component instead of ReadLabelText*/}
                            <ReadLabelText
                                auditTrailType={this.props.auditTrailData?.type}
                                maxRows={this.maxRowCount}
                                _hasValue={!!value}
                                data-testid={TestIds.EditableTextReadLabel}>
                                {value || this.props.placeholder}
                            </ReadLabelText>
                        </ReadLabel>
                    }
                    {this.isInEdit &&
                        <TextAreaWrapper>
                            <TextArea
                                maxRows={this.maxRowCount}
                                passRef={composeRefHandlers(this.textAreaRef, this.props.passRef)}
                                name={this.props.name}
                                value={value}
                                onChange={this.handleChange}
                                onBlur={this.handleBlur}
                                error={this.props.error}
                                tooltip={this.props.tooltip}/>
                        </TextAreaWrapper>
                    }
                    {!this.props.isReadOnly && this.props.showChange && <InputStatusStraight status={Status.Warning}/>}
                </Wrapper>
                {this.props.isConfirmable &&
                    <EditableTextConfirmButtons _visible={this.isInEdit}
                                                passRef={this.buttonsWrapper}
                                                onCancel={this.handleCancel}
                                                onConfirm={this.handleConfirm}
                                                onBlur={this.handleButtonsBlur}/>
                }
            </StyledEditableText>
        );
    }

    render(): React.ReactElement {
        if (this.props.isSkeleton) {
            return <Skeleton width={"120px"} height={"16px"}/>;
        }

        return this.props.isReadOnly && !this.props.auditTrailData?.type ? (<ReadOnlyText {...this.props} />) : this.renderEditableText();
    }
}

export default withTranslation(["Components", "Common"])(EditableText);
